import { LEGAL_DOCUMENTS, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, type Locale, type MeResponse } from '@dbrain/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type ClipboardEvent, type FormEvent, type InputHTMLAttributes, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { api, isApiError } from '../../api/client.js';
import { keys } from '../../api/hooks.js';
import { useCapabilities, useMeta } from '../../experience.js';
import { takeHashToken } from '../../hash-token.js';
import { currentLocale, setLocale } from '../../i18n/index.js';
import { AlertIcon, clock, EyeIcon, failureOf, SignInLayout, useSignedIn, useWait } from '../SignInPage.js';

/**
 * Phase 16a (ADR-0023 §3, D-258…D-261; 06 §28.3): the bireysel edition's sign-in, sign-up, e-mail code and password
 * reset, in the kurumsal sign-in's layout and look (SignInPage.tsx, signin.css; B2cKayit.dc.html,
 * B2cDogrulama.dc.html): the black half with the logo, the form on the right. A sign-up waits for the six digits
 * mailed to the address; until they come back the tab keeps the sign-up's key, the address and the name, never the
 * password.
 */

const CODE_LENGTH = 6;
/** a new code a minute after the last one at the earliest (the API's SIGNUP_RESEND_SECONDS) */
const RESEND_SECONDS = 60;
/** the tab's pending sign-up (sessionStorage) */
const PENDING_SIGNUP = 'oppy.signup';
const EMPTY_CODE: readonly string[] = Array.from({ length: CODE_LENGTH }, () => '');
/** a loose look before sending; the API checks the address itself */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** What the code page needs of a sign-up: the browser's key to it, the address and name it shows, when the last code went */
export interface PendingSignup {
  registration: string;
  email: string;
  displayName: string;
  sentAt: number;
}

/** A pending sign-up read back (the tab's storage, a page's history state); anything else is none */
export function pendingSignupOf(value: unknown): PendingSignup | null {
  if (!value || typeof value !== 'object') return null;
  const { registration, email, displayName, sentAt } = value as Record<string, unknown>;
  if (typeof registration !== 'string' || !registration || typeof email !== 'string' || typeof displayName !== 'string') return null;
  return { registration, email, displayName, sentAt: typeof sentAt === 'number' && Number.isFinite(sentAt) ? sentAt : 0 };
}

function readPendingSignup(): PendingSignup | null {
  try {
    const saved = sessionStorage.getItem(PENDING_SIGNUP);
    return saved ? pendingSignupOf(JSON.parse(saved)) : null;
  } catch {
    // storage unavailable (private mode) or not ours
    return null;
  }
}

function keepPendingSignup(signup: PendingSignup): void {
  try {
    sessionStorage.setItem(PENDING_SIGNUP, JSON.stringify(signup));
  } catch {
    // storage unavailable: the code page still has the sign-up in its history state
  }
}

function forgetPendingSignup(): void {
  try {
    sessionStorage.removeItem(PENDING_SIGNUP);
  } catch {
    // ignore
  }
}

/**
 * Digits typed, pasted or filled in by the browser (a one-time code lands whole in one box) go into the boxes from
 * `at` on; a whole code fills all six from the first. `next`: the box to go to.
 */
export function fillCode(code: readonly string[], at: number, text: string): { code: string[]; next: number } {
  const digits = [...text.replace(/\D/g, '')];
  const out = [...code];
  if (digits.length === 0) return { code: out, next: at };
  const start = digits.length >= CODE_LENGTH ? 0 : at;
  const placed = digits.slice(0, CODE_LENGTH - start);
  placed.forEach((digit, n) => {
    out[start + n] = digit;
  });
  return { code: out, next: Math.min(start + placed.length, CODE_LENGTH - 1) };
}

/** Whole seconds from `now` until `until` (epoch ms); 0 once it has passed */
export function secondsLeft(until: number, now: number): number {
  return Math.max(0, Math.ceil((until - now) / 1000));
}

type SignupField = 'name' | 'email' | 'password' | 'terms';
type FieldProblem = 'nameMissing' | 'nameInvalid' | 'email' | 'passwordMissing' | 'passwordShort' | 'passwordLong' | 'passwordBreached' | 'terms';
type Problems = Partial<Record<SignupField, FieldProblem>>;
const SIGNUP_FIELDS: readonly SignupField[] = ['name', 'email', 'password', 'terms'];

/** A new password before it is sent (the API's policy checks the length again, and common passwords) */
export function passwordProblemOf(password: string): FieldProblem | null {
  if (!password) return 'passwordMissing';
  if (password.length < PASSWORD_MIN_LENGTH) return 'passwordShort';
  if (password.length > PASSWORD_MAX_LENGTH) return 'passwordLong';
  return null;
}

/** What the sign-up form says before it sends anything */
export function signupProblems(form: { name: string; email: string; password: string; terms: boolean }): Problems {
  const problems: Problems = {};
  if (!form.name.trim()) problems.name = 'nameMissing';
  if (!EMAIL_SHAPE.test(form.email.trim())) problems.email = 'email';
  const password = passwordProblemOf(form.password);
  if (password) problems.password = password;
  if (!form.terms) problems.terms = 'terms';
  return problems;
}

/** The password policy's refusal, shown at the password field */
function policyProblem(error: unknown): FieldProblem | null {
  if (isApiError(error, 'password_too_short')) return 'passwordShort';
  if (isApiError(error, 'password_too_long')) return 'passwordLong';
  if (isApiError(error, 'password_breached')) return 'passwordBreached';
  return null;
}

/** The fields whose form the API refused */
function refusedFields(error: unknown): string[] {
  return isApiError(error, 'validation_failed') ? (error.fields ?? []).map((f) => f.path) : [];
}

/** A refused sign-up at its fields (the password policy, the form's checks); anything else is a banner */
function serverProblems(error: unknown): Problems {
  const problems: Problems = {};
  const policy = policyProblem(error);
  if (policy) problems.password = policy;
  const fields = refusedFields(error);
  if (fields.includes('displayName')) problems.name = 'nameInvalid';
  if (fields.includes('email')) problems.email = 'email';
  if (fields.includes('password')) problems.password = 'passwordShort';
  if (fields.includes('acceptTerms')) problems.terms = 'terms';
  return problems;
}

/** The browser's key to the sign-up is no longer known (finished, older than a day, or not one) */
const registrationGone = (error: unknown) => isApiError(error, 'registration_unknown') || refusedFields(error).includes('registration');

const joinIds = (...ids: (string | false | null | undefined)[]) => ids.filter(Boolean).join(' ') || undefined;

/** "42 saniye sonra yeniden deneyin.": what to do after too many attempts, as the wait counts down */
function waitWords(t: Translate, wait: number): string {
  if (wait <= 0) return t('b2cAuth.waitOver');
  return wait < 60 ? t('b2cAuth.wait_seconds', { count: wait }) : t('b2cAuth.wait_minutes', { count: Math.ceil(wait / 60) });
}

/** The seconds until `until` (epoch ms), counting down once a second */
function useSecondsUntil(until: number): number {
  const [, tick] = useState(0);
  useEffect(() => {
    if (until <= Date.now()) return;
    const timer = setInterval(() => {
      tick((n) => n + 1);
      if (Date.now() >= until) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [until]);
  return secondsLeft(until, Date.now());
}

/** A view that replaces a form (the link sent, the password changed): focus moves to it so a screen reader reads it */
function useFocusOnShow<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return ref;
}

/** The sign-in pages' banner (signin__alert): a strong first sentence, then what to do */
function Banner({ title, children, id, testId }: { title: string; children: ReactNode; id?: string; testId: string }) {
  return (
    <div className="signin__alert" role="alert" id={id} data-testid={testId}>
      <AlertIcon />
      <span>
        <strong>{title}</strong> {children}
      </span>
    </div>
  );
}

/**
 * A refused request that is not one field's problem (SignInPage's failureOf: only a server that does not answer
 * "can't be reached"); after too many attempts the wait counts down.
 */
function Failure({ error, wait, id, testId = 'signin-error' }: { error: unknown; wait: number; id?: string; testId?: string }) {
  const { t } = useTranslation();
  const { key } = failureOf(error);
  const timed = key === 'locked' && isApiError(error, 'rate_limited') && error.retryAfter !== undefined;
  return (
    <Banner id={id} testId={testId} title={t(`b2cAuth.errors.${key}.title`)}>
      {timed ? waitWords(t, wait) : t(`b2cAuth.errors.${key}.action`)}
    </Banner>
  );
}

type InputProps = Pick<InputHTMLAttributes<HTMLInputElement>, 'type' | 'autoComplete' | 'autoCapitalize' | 'spellCheck'>;

/** A field of the sign-in pages; its problem under it, read with it */
function TextField({
  label,
  value,
  onChange,
  problem,
  invalid,
  describedBy,
  inputRef,
  testId,
  ...input
}: InputProps & {
  label: string;
  value: string;
  onChange: (value: string) => void;
  problem?: string | null;
  /** refused without a message of its own (the banner says why) */
  invalid?: boolean;
  describedBy?: string;
  inputRef?: Ref<HTMLInputElement>;
  testId: string;
}) {
  const id = useId();
  return (
    <div className="signin__field">
      <label htmlFor={id}>{label}</label>
      <input
        {...input}
        ref={inputRef}
        id={id}
        className="signin__input"
        required
        value={value}
        aria-invalid={problem || invalid ? true : undefined}
        aria-describedby={joinIds(problem && `${id}-problem`, describedBy)}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
      {problem ? (
        <span id={`${id}-problem`} className="signin__hint signin__hint--error b2c-auth__problem" role="alert">
          {problem}
        </span>
      ) : null}
    </div>
  );
}

/** The password, with "Şifreyi göster" (as the kurumsal sign-in's) and a hint or its problem under it */
function PasswordField({
  label,
  value,
  onChange,
  problem,
  invalid,
  hint,
  describedBy,
  inputRef,
  autoComplete,
  testId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  problem?: string | null;
  invalid?: boolean;
  hint?: string;
  describedBy?: string;
  inputRef?: Ref<HTMLInputElement>;
  autoComplete: 'current-password' | 'new-password';
  testId: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [shown, setShown] = useState(false);
  const note = problem || hint;
  return (
    <div className="signin__field">
      <label htmlFor={id}>{label}</label>
      <div className="signin__password">
        <input
          ref={inputRef}
          id={id}
          className="signin__input"
          type={shown ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoCapitalize="none"
          spellCheck={false}
          required
          value={value}
          aria-invalid={problem || invalid ? true : undefined}
          aria-describedby={joinIds(note && `${id}-note`, describedBy)}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
        <button
          type="button"
          className="signin__reveal"
          aria-label={shown ? t('b2cAuth.hidePassword') : t('b2cAuth.showPassword')}
          aria-pressed={shown}
          onClick={() => setShown((v) => !v)}
          data-testid="signin-reveal"
        >
          <EyeIcon off={shown} />
        </button>
      </div>
      {/* a problem is a new element, so that it is announced; the hint is read with the field */}
      {problem ? (
        <span key="problem" id={`${id}-note`} className="signin__hint signin__hint--error b2c-auth__problem" role="alert">
          {problem}
        </span>
      ) : hint ? (
        <span key="hint" id={`${id}-note`} className="signin__hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/** Sign-in with the address and the password; "30 gün hatırla" is ticked by default (D-260) */
export function PersonalLoginPage() {
  const { t } = useTranslation();
  const signedIn = useSignedIn();
  const capabilities = useCapabilities();
  const problemId = useId();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [checked, setChecked] = useState<{ email?: FieldProblem; password?: FieldProblem }>({});
  const login = useMutation({
    mutationFn: () => api<MeResponse>('POST', '/api/auth/login', { email: email.trim(), password, remember }),
    onSuccess: signedIn,
    onError: (error) => {
      // a refused address is corrected; a refused password is typed again
      if (refusedFields(error).includes('email')) emailRef.current?.focus();
      else if (failureOf(error).field) {
        setPassword('');
        passwordRef.current?.focus();
      }
    },
  });
  const wait = useWait(login.error);
  const emailRefused = refusedFields(login.error).includes('email');
  const failure = login.error && !emailRefused ? failureOf(login.error) : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (login.isPending || wait > 0) return;
    const found = { email: EMAIL_SHAPE.test(email.trim()) ? undefined : ('email' as const), password: password ? undefined : ('passwordMissing' as const) };
    setChecked(found);
    if (found.email) emailRef.current?.focus();
    else if (found.password) passwordRef.current?.focus();
    else login.mutate();
  };
  const emailProblem = checked.email ?? (emailRefused ? 'email' : undefined);
  return (
    <SignInLayout footer={t('b2cAuth.passwordFooter')}>
      <header className="signin__head">
        <h1>{t('b2cAuth.login.title')}</h1>
      </header>
      <form className="signin__form" onSubmit={submit} noValidate>
        {failure ? <Failure error={login.error} wait={wait} id={problemId} /> : null}
        <TextField
          label={t('b2cAuth.email')}
          type="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          onChange={(value) => {
            setEmail(value);
            setChecked((before) => ({ ...before, email: undefined }));
            if (emailRefused) login.reset();
          }}
          problem={emailProblem ? t(`b2cAuth.fields.${emailProblem}`) : null}
          invalid={failure?.field}
          describedBy={failure ? problemId : undefined}
          inputRef={emailRef}
          testId="login-email"
        />
        <PasswordField
          label={t('b2cAuth.password')}
          autoComplete="current-password"
          value={password}
          onChange={(value) => {
            setPassword(value);
            setChecked((before) => ({ ...before, password: undefined }));
          }}
          problem={checked.password ? t(`b2cAuth.fields.${checked.password}`) : null}
          invalid={failure?.field}
          describedBy={failure ? problemId : undefined}
          inputRef={passwordRef}
          testId="login-password"
        />
        <div className="signin__row">
          <label className="signin__check">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-remember" />
            {t('b2cAuth.login.remember')}
          </label>
          <Link to="/forgot-password" className="signin__link b2c-auth__link" data-testid="login-forgot">
            {t('b2cAuth.login.forgot')}
          </Link>
        </div>
        <button type="submit" className="signin__submit" disabled={login.isPending || wait > 0} aria-busy={login.isPending || undefined} data-testid="login-submit">
          {wait > 0 ? t('b2cAuth.countdown', { label: t('b2cAuth.login.submit'), time: clock(wait) }) : t('b2cAuth.login.submit')}
        </button>
        {capabilities.publicSignup ? (
          <p className="b2c-auth__switch">
            {t('b2cAuth.login.noAccount')}{' '}
            <Link to="/signup" data-testid="login-signup">
              {t('b2cAuth.login.signup')}
            </Link>
          </p>
        ) : null}
      </form>
    </SignInLayout>
  );
}

interface SignupRequest {
  displayName: string;
  email: string;
  password: string;
  acceptTerms: true;
  marketingConsent: boolean;
  locale: Locale;
}

/**
 * B2cKayit: name, address, password; the Kullanım Koşulları box is required (the API records its version), the
 * campaign e-mails are optional and unticked, KVKK is a line of information (D-259). The answer is the same whether
 * or not the address has an account; the code page follows.
 */
export function PersonalSignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const problemId = useId();
  const termsProblemId = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const termsRef = useRef<HTMLInputElement>(null);
  // back from the code page ("E-postayı değiştir"): the name and the address are kept, the password never
  const [kept] = useState(() => readPendingSignup());
  const [name, setName] = useState(kept?.displayName ?? '');
  const [email, setEmail] = useState(kept?.email ?? '');
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [checked, setChecked] = useState<Problems>({});
  const focusField = (field: SignupField) => ({ name: nameRef, email: emailRef, password: passwordRef, terms: termsRef })[field].current?.focus();
  const signup = useMutation({
    mutationFn: (body: SignupRequest) => api<{ status: 'check_email'; registration: string }>('POST', '/api/auth/signup', body),
    onSuccess: (answer, body) => {
      const pending: PendingSignup = { registration: answer.registration, email: body.email, displayName: body.displayName, sentAt: Date.now() };
      keepPendingSignup(pending);
      void navigate('/signup/verify', { state: { signup: pending } });
    },
    onError: (error) => {
      const refused = serverProblems(error);
      const first = SIGNUP_FIELDS.find((field) => refused[field]);
      if (first) focusField(first);
    },
  });
  const wait = useWait(signup.error);
  const server = serverProblems(signup.error);
  const problems: Problems = { ...server, ...checked };
  const banner = signup.error !== null && Object.keys(server).length === 0;
  const changeEmail = (location.state as { changeEmail?: boolean } | null)?.changeEmail === true;
  useEffect(() => {
    if (!changeEmail) return;
    emailRef.current?.focus();
    emailRef.current?.select();
  }, [changeEmail]);

  /** an edit takes that field's problem away */
  const edited = (field: SignupField) => {
    setChecked((before) => (before[field] ? { ...before, [field]: undefined } : before));
    if (server[field]) signup.reset();
  };
  const problemText = (field: SignupField) => {
    const problem = problems[field];
    return problem ? t(`b2cAuth.fields.${problem}`) : null;
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (signup.isPending || wait > 0) return;
    const found = signupProblems({ name, email, password, terms });
    setChecked(found);
    const first = SIGNUP_FIELDS.find((field) => found[field]);
    if (first) {
      focusField(first);
      return;
    }
    signup.mutate({ displayName: name.trim(), email: email.trim(), password, acceptTerms: true, marketingConsent: marketing, locale: currentLocale() });
  };
  return (
    <SignInLayout footer={t('b2cAuth.passwordFooter')}>
      <header className="signin__head">
        <h1>{t('b2cAuth.signup.title')}</h1>
        <p className="b2c-auth__lead">{t('b2cAuth.signup.subtitle')}</p>
      </header>
      <form className="signin__form" onSubmit={submit} noValidate>
        {banner ? <Failure error={signup.error} wait={wait} id={problemId} /> : null}
        <TextField
          label={t('b2cAuth.signup.name')}
          autoComplete="name"
          value={name}
          onChange={(value) => {
            setName(value);
            edited('name');
          }}
          problem={problemText('name')}
          inputRef={nameRef}
          testId="signup-name"
        />
        <TextField
          label={t('b2cAuth.email')}
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          onChange={(value) => {
            setEmail(value);
            edited('email');
          }}
          problem={problemText('email')}
          inputRef={emailRef}
          testId="signup-email"
        />
        <PasswordField
          label={t('b2cAuth.password')}
          autoComplete="new-password"
          hint={t('b2cAuth.passwordHint')}
          value={password}
          onChange={(value) => {
            setPassword(value);
            edited('password');
          }}
          problem={problemText('password')}
          inputRef={passwordRef}
          testId="signup-password"
        />
        <div className="b2c-auth__consents">
          <label className="b2c-auth__consent">
            <input
              ref={termsRef}
              type="checkbox"
              checked={terms}
              required
              aria-invalid={problems.terms ? true : undefined}
              aria-describedby={problems.terms ? termsProblemId : undefined}
              onChange={(e) => {
                setTerms(e.target.checked);
                edited('terms');
              }}
              data-testid="signup-terms"
            />
            <span>
              <Trans
                i18nKey="b2cAuth.signup.terms"
                components={{ terms: <a href={`/yasal/${LEGAL_DOCUMENTS.terms.slug}`} target="_blank" rel="noopener noreferrer" data-testid="signup-terms-link" /> }}
              />
            </span>
          </label>
          {problems.terms ? (
            <span id={termsProblemId} className="signin__hint signin__hint--error b2c-auth__problem" role="alert" data-testid="signup-terms-error">
              {t('b2cAuth.fields.terms')}
            </span>
          ) : null}
          <label className="b2c-auth__consent">
            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} data-testid="signup-marketing" />
            <span>
              {t('b2cAuth.signup.marketing')} <span className="b2c-auth__optional">{t('b2cAuth.signup.optional')}</span>
            </span>
          </label>
          <p className="b2c-auth__privacy">
            <Trans
              i18nKey="b2cAuth.signup.privacy"
              components={{ privacy: <a href={`/yasal/${LEGAL_DOCUMENTS.privacy.slug}`} target="_blank" rel="noopener noreferrer" data-testid="signup-privacy-link" /> }}
            />
          </p>
        </div>
        <button type="submit" className="signin__submit" disabled={signup.isPending || wait > 0} aria-busy={signup.isPending || undefined} data-testid="signup-submit">
          {wait > 0 ? t('b2cAuth.countdown', { label: t('b2cAuth.signup.submit'), time: clock(wait) }) : t('b2cAuth.signup.submit')}
        </button>
        <p className="b2c-auth__switch">
          {t('b2cAuth.signup.haveAccount')}{' '}
          <Link to="/login" data-testid="signup-login">
            {t('b2cAuth.signup.login')}
          </Link>
        </p>
      </form>
    </SignInLayout>
  );
}

/** "yeniden gönderin (0:42)": asks for a new code; waits a minute after each one */
function ResendButton({ children, wait, busy, onResend }: { children?: ReactNode; wait: number; busy: boolean; onResend: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="b2c-auth__inline-link" disabled={wait > 0 || busy} aria-busy={busy || undefined} onClick={onResend} data-testid="verify-resend">
      {children}
      {wait > 0 ? ` ${t('b2cAuth.verify.resendWait', { time: clock(wait) })}` : null}
    </button>
  );
}

/**
 * B2cDogrulama: the six digits mailed to the address (10 minutes, at most 5 wrong tries; D-258). A whole code typed,
 * pasted or filled in by the browser goes by itself; the right one opens the account and signs in.
 */
export function PersonalVerifyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const address = useMeta().data?.mailFrom;
  const problemId = useId();
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  // the tab's storage; without it, the state the sign-up page came here with
  const [signup, setSignup] = useState(() => readPendingSignup() ?? pendingSignupOf((location.state as { signup?: unknown } | null)?.signup));
  const [gone, setGone] = useState(false);
  const [code, setCode] = useState<string[]>(() => [...EMPTY_CODE]);
  const [incomplete, setIncomplete] = useState(false);
  const [sentAgain, setSentAgain] = useState(false);
  // a reload keeps the countdown: it starts from when the last code went
  const [resendAt, setResendAt] = useState(() => (signup ? signup.sentAt + RESEND_SECONDS * 1000 : 0));
  const resendWait = useSecondsUntil(resendAt);

  const focusDigit = (at: number) => {
    const box = inputs.current[Math.max(0, Math.min(CODE_LENGTH - 1, at))];
    box?.focus();
    box?.select();
  };

  const verify = useMutation({
    mutationFn: (value: string) => api<MeResponse>('POST', '/api/auth/signup/verify', { registration: signup?.registration, code: value }),
    onSuccess: async (me) => {
      forgetPendingSignup();
      queryClient.setQueryData(keys.me, me);
      await setLocale(me.user.locale);
      void navigate('/', { replace: true });
    },
    onError: (error) => {
      if (registrationGone(error)) {
        setGone(true);
        return;
      }
      // a wrong or spent code is typed again from the first box
      if (isApiError(error, 'code_invalid') || isApiError(error, 'code_expired')) {
        setCode([...EMPTY_CODE]);
        focusDigit(0);
      }
    },
  });
  const resend = useMutation({
    mutationFn: () => api<{ status: 'sent'; retryAfter?: number }>('POST', '/api/auth/signup/resend', { registration: signup?.registration }),
    onSuccess: (answer) => {
      const sentAt = Date.now();
      if (signup) {
        const next = { ...signup, sentAt };
        keepPendingSignup(next);
        setSignup(next);
      }
      setResendAt(sentAt + (answer?.retryAfter ?? RESEND_SECONDS) * 1000);
      setSentAgain(true);
      setIncomplete(false);
      verify.reset();
      setCode([...EMPTY_CODE]);
      focusDigit(0);
    },
    onError: (error) => {
      if (registrationGone(error)) setGone(true);
      else if (isApiError(error, 'rate_limited') && error.retryAfter) setResendAt(Date.now() + error.retryAfter * 1000);
    },
  });

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  const codeRefused = isApiError(verify.error, 'code_invalid') || isApiError(verify.error, 'code_expired');
  // a minute's wait for the next code shows on the link itself; a longer one (too many codes) also in the banner
  const resendFailure =
    resend.error && !registrationGone(resend.error) && !(isApiError(resend.error, 'rate_limited') && (resend.error.retryAfter ?? 0) <= RESEND_SECONDS) ? resend.error : null;
  const failure = verify.error && !codeRefused ? verify.error : resendFailure;
  const wait = useWait(failure);
  const verifyWaits = isApiError(verify.error, 'rate_limited') && wait > 0;
  // the boxes stay marked until a new digit is typed
  const invalid = incomplete || (codeRefused && code.every((digit) => !digit));

  if (!signup || gone) {
    return (
      <SignInLayout footer={t('b2cAuth.verify.footer')}>
        <header className="signin__head b2c-auth__verify">
          <h1>{t('b2cAuth.verify.title')}</h1>
        </header>
        <Banner testId="verify-error" title={t('b2cAuth.verify.errors.unknown.title')}>
          <Trans i18nKey="b2cAuth.verify.errors.unknown.action" components={{ signup: <Link to="/signup" className="b2c-auth__link" data-testid="verify-signup" /> }} />
        </Banner>
      </SignInLayout>
    );
  }

  const send = (value: readonly string[]) => {
    if (verify.isPending || verifyWaits) return;
    const empty = value.findIndex((digit) => !digit);
    if (empty >= 0) {
      setIncomplete(true);
      focusDigit(empty);
      return;
    }
    setIncomplete(false);
    setSentAgain(false);
    verify.mutate(value.join(''));
  };
  const put = (at: number, text: string) => {
    if (verify.isPending) return;
    const { code: next, next: target } = fillCode(code, at, text);
    setCode(next);
    setIncomplete(false);
    focusDigit(target);
    if (next.every((digit) => digit) && next.join('') !== code.join('')) send(next);
  };
  const clearDigit = (at: number) => setCode((before) => before.map((digit, n) => (n === at ? '' : digit)));
  const changed = (at: number, box: HTMLInputElement) => {
    const value = box.value;
    if (!value) {
      clearDigit(at);
      return;
    }
    const before = code[at] ?? '';
    // typed into a box that had a digit, with the caret after it: the new digit is the one before the caret
    const typed = before && value.length === before.length + 1 ? value.charAt((box.selectionStart ?? value.length) - 1) : value;
    put(at, typed);
  };
  const keyDown = (at: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      setIncomplete(false);
      if (code[at] || event.key === 'Delete') clearDigit(at);
      else if (at > 0) {
        // an empty box: back to the one before, and its digit goes
        clearDigit(at - 1);
        focusDigit(at - 1);
      }
    } else if (event.key === 'ArrowLeft' && at > 0) {
      event.preventDefault();
      focusDigit(at - 1);
    } else if (event.key === 'ArrowRight' && at < CODE_LENGTH - 1) {
      event.preventDefault();
      focusDigit(at + 1);
    }
  };
  const pasted = (at: number, event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    put(at, event.clipboardData.getData('text'));
  };

  let problem: ReactNode = null;
  if (incomplete) {
    problem = (
      <Banner id={problemId} testId="verify-error" title={t('b2cAuth.verify.errors.incomplete.title')}>
        {t('b2cAuth.verify.errors.incomplete.action')}
      </Banner>
    );
  } else if (isApiError(verify.error, 'code_invalid')) {
    const left = verify.error.attemptsLeft;
    problem = (
      <Banner id={problemId} testId="verify-error" title={t('b2cAuth.verify.errors.invalid.title')}>
        {left !== undefined ? t('b2cAuth.verify.errors.invalid.action', { count: left }) : t('b2cAuth.verify.errors.invalid.retype')}
      </Banner>
    );
  } else if (isApiError(verify.error, 'code_expired')) {
    problem = (
      <Banner id={problemId} testId="verify-error" title={t('b2cAuth.verify.errors.expired.title')}>
        {t('b2cAuth.verify.errors.expired.action')}
      </Banner>
    );
  } else if (failure) {
    problem = <Failure error={failure} wait={wait} id={problemId} testId="verify-error" />;
  }

  return (
    <SignInLayout footer={t('b2cAuth.verify.footer')}>
      <header className="signin__head b2c-auth__verify">
        <h1>{t('b2cAuth.verify.title')}</h1>
        <p className="b2c-auth__lead">{t('b2cAuth.verify.subtitle', { email: signup.email })}</p>
      </header>
      <form
        className="signin__form b2c-auth__code-form"
        onSubmit={(e) => {
          e.preventDefault();
          send(code);
        }}
        noValidate
        aria-busy={verify.isPending || undefined}
      >
        {problem}
        <fieldset className="b2c-auth__code">
          <legend className="ui-visually-hidden">{t('b2cAuth.verify.code')}</legend>
          <div className="b2c-auth__digits">
            {code.map((digit, at) => (
              <input
                key={at}
                ref={(box) => {
                  inputs.current[at] = box;
                }}
                className="b2c-auth__digit"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={at === 0 ? 'one-time-code' : 'off'}
                aria-label={t('b2cAuth.verify.digit', { n: at + 1 })}
                aria-invalid={invalid || undefined}
                aria-describedby={problem ? problemId : undefined}
                value={digit}
                onChange={(e) => changed(at, e.currentTarget)}
                onKeyDown={(e) => keyDown(at, e)}
                onPaste={(e) => pasted(at, e)}
                onFocus={(e) => e.currentTarget.select()}
                data-testid={`verify-digit-${at}`}
              />
            ))}
          </div>
        </fieldset>
        <button type="submit" className="signin__submit" disabled={verify.isPending || verifyWaits} aria-busy={verify.isPending || undefined} data-testid="verify-submit">
          {verifyWaits ? t('b2cAuth.countdown', { label: t('b2cAuth.verify.submit'), time: clock(wait) }) : t('b2cAuth.verify.submit')}
        </button>
        <p className="b2c-auth__help">
          <Trans
            i18nKey="b2cAuth.verify.help"
            components={{
              resend: (
                <ResendButton
                  wait={resendWait}
                  busy={resend.isPending}
                  onResend={() => {
                    setSentAgain(false);
                    resend.mutate();
                  }}
                />
              ),
              change: <Link to="/signup" state={{ changeEmail: true }} className="b2c-auth__inline-link" data-testid="verify-change-email" />,
            }}
          />
        </p>
        <p className="b2c-auth__sent" role="status">
          {sentAgain ? <span data-testid="verify-sent">{t('b2cAuth.verify.sent')}</span> : null}
        </p>
        <div className="signin__note" role="note">
          <EnvelopeIcon />
          <span>{address ? <Trans i18nKey="b2cAuth.verify.info" values={{ address }} components={{ b: <strong /> }} /> : t('b2cAuth.verify.infoShort')}</span>
        </div>
      </form>
    </SignInLayout>
  );
}

/** The link is on its way (or not, if the address has no account: the page cannot tell) */
function ForgotDone({ email }: { email: string }) {
  const { t } = useTranslation();
  const heading = useFocusOnShow<HTMLHeadingElement>();
  return (
    <>
      <header className="signin__head">
        <h1 ref={heading} tabIndex={-1} className="b2c-auth__focus">
          {t('b2cAuth.forgot.doneTitle')}
        </h1>
      </header>
      <div className="signin__note b2c-auth__message" data-testid="forgot-done">
        <EnvelopeIcon />
        <span>
          <Trans i18nKey="b2cAuth.forgot.done" values={{ email }} components={{ b: <strong /> }} /> {t('b2cAuth.forgot.spam')}
        </span>
      </div>
      <p className="b2c-auth__switch">
        <Link to="/login" data-testid="forgot-back">
          {t('b2cAuth.forgot.back')}
        </Link>
      </p>
    </>
  );
}

/** "Şifremi unuttum": a link to set a new password goes to the address if it has an account; the answer is the same either way */
export function PersonalForgotPasswordPage() {
  const { t } = useTranslation();
  const problemId = useId();
  const emailRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [checked, setChecked] = useState(false);
  const request = useMutation({
    mutationFn: (address: string) => api<unknown>('POST', '/api/auth/password-reset/request', { email: address }),
    onError: (error) => {
      if (refusedFields(error).includes('email')) emailRef.current?.focus();
    },
  });
  const wait = useWait(request.error);
  const emailRefused = refusedFields(request.error).includes('email');
  const failure = request.error && !emailRefused ? request.error : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (request.isPending || wait > 0) return;
    const wrong = !EMAIL_SHAPE.test(email.trim());
    setChecked(wrong);
    if (wrong) emailRef.current?.focus();
    else request.mutate(email.trim());
  };
  return (
    <SignInLayout footer={t('b2cAuth.passwordFooter')}>
      {request.isSuccess ? (
        <ForgotDone email={request.variables ?? email.trim()} />
      ) : (
        <>
          <header className="signin__head">
            <h1>{t('b2cAuth.forgot.title')}</h1>
            <p className="b2c-auth__lead">{t('b2cAuth.forgot.subtitle')}</p>
          </header>
          <form className="signin__form" onSubmit={submit} noValidate>
            {failure ? <Failure error={failure} wait={wait} id={problemId} /> : null}
            <TextField
              label={t('b2cAuth.email')}
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(value) => {
                setEmail(value);
                setChecked(false);
                if (emailRefused) request.reset();
              }}
              problem={checked || emailRefused ? t('b2cAuth.fields.email') : null}
              describedBy={failure ? problemId : undefined}
              inputRef={emailRef}
              testId="forgot-email"
            />
            <button type="submit" className="signin__submit" disabled={request.isPending || wait > 0} aria-busy={request.isPending || undefined} data-testid="forgot-submit">
              {wait > 0 ? t('b2cAuth.countdown', { label: t('b2cAuth.forgot.submit'), time: clock(wait) }) : t('b2cAuth.forgot.submit')}
            </button>
            <p className="b2c-auth__switch">
              <Link to="/login" data-testid="forgot-back">
                {t('b2cAuth.forgot.back')}
              </Link>
            </p>
          </form>
        </>
      )}
    </SignInLayout>
  );
}

/** The new password is set; every session ended with the old one */
function ResetDone() {
  const note = useFocusOnShow<HTMLDivElement>();
  return (
    <div ref={note} tabIndex={-1} className="signin__note b2c-auth__message b2c-auth__message--done b2c-auth__focus" data-testid="reset-done">
      <CheckIcon />
      <span>
        <Trans i18nKey="b2cAuth.reset.done" components={{ login: <Link to="/login" className="b2c-auth__link" data-testid="reset-login" /> }} />
      </span>
    </div>
  );
}

/** The e-mailed link's page (#token=…, read once and taken out of the address bar): a new password */
export function PersonalResetPasswordPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const problemId = useId();
  const passwordRef = useRef<HTMLInputElement>(null);
  const [token] = useState(() => takeHashToken());
  const [password, setPassword] = useState('');
  const [checked, setChecked] = useState<FieldProblem | null>(null);
  const reset = useMutation({
    mutationFn: () => api<unknown>('POST', '/api/auth/password-reset/confirm', { token, password }),
    onSuccess: () => {
      // every session ended with the old password: what this browser kept of them goes too (the installation's meta stays)
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'meta' });
      queryClient.setQueryData(keys.me, null);
    },
    onError: (error) => {
      if (policyProblem(error) || refusedFields(error).includes('password')) passwordRef.current?.focus();
    },
  });
  const wait = useWait(reset.error);
  const serverProblem = policyProblem(reset.error) ?? (refusedFields(reset.error).includes('password') ? 'passwordShort' : null);
  const problem = checked ?? serverProblem;
  const linkInvalid = !token || isApiError(reset.error, 'token_invalid') || refusedFields(reset.error).includes('token');
  const failure = reset.error && !serverProblem ? reset.error : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (reset.isPending || wait > 0) return;
    const found = passwordProblemOf(password);
    setChecked(found);
    if (found) passwordRef.current?.focus();
    else reset.mutate();
  };
  return (
    <SignInLayout footer={t('b2cAuth.passwordFooter')}>
      <header className="signin__head">
        <h1>{t('b2cAuth.reset.title')}</h1>
      </header>
      {reset.isSuccess ? (
        <ResetDone />
      ) : linkInvalid ? (
        <Banner testId="reset-invalid" title={t('b2cAuth.reset.invalid')}>
          <Link to="/forgot-password" className="b2c-auth__link" data-testid="reset-new-link">
            {t('b2cAuth.reset.newLink')}
          </Link>
        </Banner>
      ) : (
        <form className="signin__form" onSubmit={submit} noValidate>
          {failure ? <Failure error={failure} wait={wait} id={problemId} /> : null}
          <PasswordField
            label={t('b2cAuth.newPassword')}
            autoComplete="new-password"
            hint={t('b2cAuth.passwordHint')}
            value={password}
            onChange={(value) => {
              setPassword(value);
              setChecked(null);
              if (serverProblem) reset.reset();
            }}
            problem={problem ? t(`b2cAuth.fields.${problem}`) : null}
            describedBy={failure ? problemId : undefined}
            inputRef={passwordRef}
            testId="reset-password"
          />
          <button type="submit" className="signin__submit" disabled={reset.isPending || wait > 0} aria-busy={reset.isPending || undefined} data-testid="reset-submit">
            {wait > 0 ? t('b2cAuth.countdown', { label: t('b2cAuth.reset.submit'), time: clock(wait) }) : t('b2cAuth.reset.submit')}
          </button>
        </form>
      )}
    </SignInLayout>
  );
}

function EnvelopeIcon() {
  return (
    <svg className="signin__note-icon" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="signin__note-icon" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.5 2.5 2.5 4.5-5" />
    </svg>
  );
}
